import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/BritishColumbia/Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { CannabisPrimaryRoute } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/Section9/CannabisPrimaryRoute";
import { TimesUsedUnit } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/Section9/TimesUsedUnit";

class BritishColumbiaObstetricalFileCannabisSubSection extends BaseBritishColumbiaObstetricalFileSubSection<BaseBritishColumbiaObstetricalFileSubSectionProps, never> {
    private datePickerRef: DateSelector;

    public render(): JSX.Element {
        const model = this.props.form.Section9.Cannabis;

        return (
            <tbody className="space-sm-before-tbody" style={{ whiteSpace: "nowrap" }}>
                <tr>
                    <td className="bold">{this.getString("CannabisLabel")}</td>
                    <td>
                        {this.renderInlineNoYesRadio(
                            model?.ConsumedThreeMonthsBeforePregnancy,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section9.Cannabis.ConsumedThreeMonthsBeforePregnancy = value),
                            "Section9_ConsumedThreeMonthsBeforePregnancy"
                        )}
                    </td>
                    <td>
                        {this.renderInlineNoYesRadio(
                            model?.ConsumesDuringPregnancy,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section9.Cannabis.ConsumesDuringPregnancy = value),
                            "Section9_ConsumesDuringPregnancy"
                        )}
                    </td>
                </tr>
                <tr>
                    <td>{this.getString("ObstetricalFile_CannabidiolProductsOnlyAbbrevLabel")}</td>
                    <td>
                        {this.renderInlineNoYesRadio(
                            model?.ConsumedCannabidiolOnlyThreeMonthsBeforePregnancy,
                            value => this.mutateCannabisSubSectionModel(s => s.Section9.Cannabis.ConsumedCannabidiolOnlyThreeMonthsBeforePregnancy = value, true),
                            "Section9_ConsumedCannabidiolOnlyThreeMonthsBeforePregnancy"
                        )}
                    </td>
                    <td>
                        {this.renderInlineNoYesRadio(
                            model?.ConsumesCannabidiolOnlyDuringPregnancy,
                            value => this.mutateCannabisSubSectionModel(s => s.Section9.Cannabis.ConsumesCannabidiolOnlyDuringPregnancy = value, true),
                            "Section9_ConsumesCannabidiolOnlyDuringPregnancy"
                        )}
                    </td>
                </tr>
                <tr>
                    <td>{this.getString("ObstetricalFile_NumberTimesUsedPerLabel")}</td>
                    <td>
                        <div className="inline-flex-center">
                            {this.controlRenderer.renderTextbox({
                                value: model?.TimesUsedPerUnitThreeMonthsBeforePregnancy?.toString() ?? "",
                                onChange: value => this.mutateCannabisSubSectionModel(s => s.Section9.Cannabis.TimesUsedPerUnitThreeMonthsBeforePregnancy = value && parseInt(value)),
                                isInteger: true,
                                className: "times-used-per-textbox",
                                maxLength: this.page1FieldLimits.SubstanceUse_3MonthsBeforePregnancy_CannabisUsedPer.MaxChars
                            })}
                            {this.props.readonly ? "/" : null}
                            {this.controlRenderer.renderComboBox({
                                id: "Section9_TimesUsedUnitThreeMonthsBeforePregnancy",
                                items: this.getTimeUnitComboboxItems(),
                                selectedCode: model?.TimesUsedUnitThreeMonthsBeforePregnancy,
                                onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section9.Cannabis.TimesUsedUnitThreeMonthsBeforePregnancy = TimesUsedUnit[value]),
                                className: "times-used-per-unit-combobox",
                                hasEmptyItem: true
                            })}
                        </div>
                    </td>
                    <td>
                        <div className="inline-flex-center">
                            {this.controlRenderer.renderTextbox({
                                value: model?.TimesUsedPerUnitDuringPregnancy?.toString() ?? "",
                                onChange: value => this.mutateCannabisSubSectionModel(s => s.Section9.Cannabis.TimesUsedPerUnitDuringPregnancy = value && parseInt(value)),
                                isInteger: true,
                                className: "times-used-per-textbox",
                                maxLength: this.page1FieldLimits.SubstanceUse_DuringPregnancy_CannabisUsedPer.MaxChars
                            })}
                            {this.props.readonly && model?.TimesUsedUnitDuringPregnancy ? "/" : null}
                            {this.controlRenderer.renderComboBox({
                                id: "Section9_TimesUsedUnitDuringPregnancy",
                                items: this.getTimeUnitComboboxItems(),
                                selectedCode: model?.TimesUsedUnitDuringPregnancy,
                                onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section9.Cannabis.TimesUsedUnitDuringPregnancy = TimesUsedUnit[value]),
                                className: "times-used-per-unit-combobox",
                                hasEmptyItem: true
                            })}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="align-top">{this.getString("PrimaryRouteLabel")}</td>
                    <td>
                        {this.renderRadioButtonGroup(
                            "Section9_PrimaryRouteThreeMonthsBeforePregnancy",
                            model?.PrimaryRouteThreeMonthsBeforePregnancy,
                            value => this.mutateCannabisSubSectionModel(s => s.Section9.Cannabis.PrimaryRouteThreeMonthsBeforePregnancy = CannabisPrimaryRoute[value], true),
                            this.getPrimaryRouteOptions()
                        )}
                    </td>
                    <td>
                        {this.renderRadioButtonGroup(
                            "Section9_PrimaryRouteDuringPregnancy",
                            model?.PrimaryRouteDuringPregnancy,
                            value => this.mutateCannabisSubSectionModel(
                                s => {
                                    s.Section9.Cannabis.PrimaryRouteDuringPregnancy = CannabisPrimaryRoute[value];
                                    s.Section9.Cannabis.ConsumesDuringPregnancy ??= s.Section9.Cannabis.hasSelectedAnySubFieldsOfConsumesDuringPregnancy();
                                }
                            ),
                            this.getPrimaryRouteOptions()
                        )}
                    </td>
                </tr>
                <tr>
                    <td colSpan={3}>
                        <div className="nowrap">
                            <span>{this.getString("QuitCannabisLabel")}:</span>
                            <span className="margin-sm-left">
                                {this.renderInlineNoYesRadio(
                                    model?.HasQuitCannabis,
                                    value => this.mutateCannabisSubSectionModel(
                                        s => s.Section9.Cannabis.HasQuitCannabis = value,
                                        true,
                                        () => model?.HasQuitCannabis && this.datePickerRef.open()
                                    ),
                                    "Section9_HasQuitCannabis"
                                )}
                            </span>
                            <span className="margin-md-left section9-date-container">
                                {this.renderInlineDatePicker(
                                    "DateLabel",
                                    model?.QuitCannabisDate,
                                    value => this.mutateCannabisSubSectionModel(
                                        s => {
                                            s.Section9.Cannabis.QuitCannabisDate = value;
                                            s.Section9.Cannabis.HasQuitCannabis ??= !!value;
                                        },
                                        true
                                    ),
                                    "",
                                    "normal-text",
                                    ref => this.datePickerRef = ref
                                )}
                            </span>
                        </div>
                    </td>
                </tr>
            </tbody>
        );
    }

    private renderInlineNoYesRadio(value: boolean, onChange: (value: boolean) => void, id: string): JSX.Element {
        return this.controlRenderer.renderInlineYesNoRadio(value, onChange, id, "resizable", null, true);
    }

    private getPrimaryRouteOptions(): KeyValuePair<CannabisPrimaryRoute, string>[] {
        return [
            new KeyValuePair(CannabisPrimaryRoute.Smoke, this.getString("SmokeLabel")),
            new KeyValuePair(CannabisPrimaryRoute.Vaporize, this.getString("VaporizeLabel")),
            new KeyValuePair(CannabisPrimaryRoute.EdibleSlashOral, this.getString("EdibleSlashOralLabel")),
            new KeyValuePair(CannabisPrimaryRoute.Other, this.getString("OtherLabel"))
        ];
    }

    private getTimeUnitComboboxItems(): ComboBoxDropDownItem[] {
        return [
            new ComboBoxDropDownItem(TimesUsedUnit.Day, this.getString("DayLowerCaseLabel")),
            new ComboBoxDropDownItem(TimesUsedUnit.Week, this.getString("ObstetricalFile_WeekAbbrevLabel")),
            new ComboBoxDropDownItem(TimesUsedUnit.Month, this.getString("ObstetricalFile_MonthAbbrevLabel"))
        ];
    }

    private mutateCannabisSubSectionModel(mutation: (state: BritishColumbiaObstetricalFileForm) => void, updateForm?: boolean, then?: () => void): void {
        this.props.mutateModelState(
            s => {
                mutation(s);

                if (s.Section9.Cannabis.hasSelectedAnySubFieldsOfConsumedThreeMonthsBeforePregnancy())
                    s.Section9.Cannabis.ConsumedThreeMonthsBeforePregnancy ??= true;
                if (s.Section9.Cannabis.hasSelectedAnySubFieldsOfConsumesDuringPregnancy())
                    s.Section9.Cannabis.ConsumesDuringPregnancy ??= true;
            },
            () => {
                if (updateForm)
                    this.props.handleFormUpdate();

                then?.();
            }
        );
    }
}

export { BritishColumbiaObstetricalFileCannabisSubSection };
